import IGeocoder, { GeocoderReverseResult, GeocoderResult } from '@shared/Core/Services/IGeocoder';
import IMapboxService, { IMapboxService_TYPE } from '@shared/Core/Services/IMapboxService';
import { LatLng } from '@shared/Core/Types';
import { injectable, inject } from 'inversify';
import IEnvironment, { IEnvironment_TYPE } from '@shared/Core/IEnvironment';
import IocContainer from '@shared/Core/IocContainer';

@injectable()
export default class Geocoder implements IGeocoder {
    private geocoderCache = new Map<string, GeocoderResult[]>();
    private reverseGeocoderCache = new Map<string, GeocoderReverseResult>();
    private defaultLanguage = 'cs';
    private environment: IEnvironment;
    private mapboxService: IMapboxService;

    constructor() {
        this.environment = IocContainer.get<IEnvironment>(IEnvironment_TYPE);
        this.mapboxService = IocContainer.get<IMapboxService>(IMapboxService_TYPE);
    }

    public async geocode(term: string) {
        if (this.geocoderCache.has(term)) {
            return this.geocoderCache.get(term) ?? [];
        }
        
        if (!this.environment.mapboxKey) return [];

        try {
            const url = new URL('https://api.mapbox.com/geocoding/v5/mapbox.places/' + encodeURIComponent(term) + '.json');
            url.searchParams.append('access_token', this.environment.mapboxKey);
            url.searchParams.append('language', this.defaultLanguage);
            url.searchParams.append('country', 'cz,sk');
            url.searchParams.append('types', 'address');
            url.searchParams.append('limit', '10');

            const response = await fetch(url.toString());
            const data = await response.json();

            if (!response.ok) {
                console.error('Mapbox geocoding error:', data);
                return [];
            }

            const items = data.features.map((feature: any) => ({
                id: feature.id,
                label: feature.place_name,
                location: {
                    lat: feature.center[1],
                    lng: feature.center[0],
                },
            } as GeocoderResult));

            this.geocoderCache.set(term, items);
            return items;
        } catch (error) {
            console.error('Error during geocoding:', error);
            return [];
        }
    }

    public async reverseGeocode(location: LatLng, lang: string) {
        const locationKey = `${location.lat};${location.lng}`;

        if (this.reverseGeocoderCache.has(locationKey)) {
            const cached = this.reverseGeocoderCache.get(locationKey);
            if (!cached) { throw Error('Invalid cached result.'); }
            return cached;
        }

        if (!this.environment.mapboxKey) return {items: [], label: 'Zjišťuji adresu'};

        try {
            const url = new URL(`https://api.mapbox.com/geocoding/v5/mapbox.places/${location.lng},${location.lat}.json`);
            url.searchParams.append('access_token', this.environment.mapboxKey);
            url.searchParams.append('language', lang || this.defaultLanguage);
            url.searchParams.append('types', 'address');
            url.searchParams.append('limit', '1');

            const response = await fetch(url.toString());
            const data = await response.json();

            if (!response.ok) {
                console.error('Mapbox reverse geocoding error:', data);
                return {items: [], label: 'Adresa nenalezena'};
            }

            const result: GeocoderReverseResult = {
                items: data.features.map((feature: any) => ({
                    // Map any properties needed for GeocoderReverseItem
                })),
                label: data.features.length > 0 ? data.features[0].place_name : 'Adresa nenalezena'
            };

            this.reverseGeocoderCache.set(locationKey, result);
            return result;
        } catch (error) {
            console.error('Error during reverse geocoding:', error);
            return {items: [], label: 'Chyba při zjišťování adresy'};
        }
    }
}
